/**
 * AiChat.js — Gemini AI Chat Assistant for PatchSphere AI
 *
 * Full-page chat interface that:
 * - Shows a live summary card at the top (online devices, vulns, compliance)
 * - Provides a chat window with conversation history
 * - Suggests quick-action prompts
 * - Connects to POST /api/ai/chat and GET /api/ai/summary
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';

// ── Styles ────────────────────────────────────────────────────────────────
const S = {
  page: {
    padding: '24px',
    maxWidth: '960px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24,
  },
  headerIcon: {
    width: 44, height: 44, background: 'linear-gradient(135deg,#1a56db,#7c3aed)',
    borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, flexShrink: 0,
  },
  headerTitle: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary, #111827)' },
  headerSub:   { fontSize: 13, color: 'var(--text-secondary, #6b7280)', marginTop: 2 },

  // Summary cards
  summaryRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 12, marginBottom: 20,
  },
  card: {
    background: 'var(--card-bg, #fff)', border: '1px solid var(--border, #e5e7eb)',
    borderRadius: 10, padding: '14px 16px',
  },
  cardLabel: { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.05em' },
  cardValue: { fontSize: 24, fontWeight: 700, marginTop: 4 },

  // Chat container
  chatWrap: {
    background: 'var(--card-bg, #fff)', border: '1px solid var(--border, #e5e7eb)',
    borderRadius: 12, display: 'flex', flexDirection: 'column', height: 520,
  },
  chatHeader: {
    padding: '14px 18px', borderBottom: '1px solid var(--border, #e5e7eb)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  chatTitle: { fontWeight: 600, fontSize: 14, color: 'var(--text-primary, #111827)', display: 'flex', alignItems: 'center', gap: 8 },
  clearBtn: {
    background: 'none', border: '1px solid var(--border, #e5e7eb)', borderRadius: 6,
    padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--text-secondary, #6b7280)',
  },

  // Messages
  messages: {
    flex: 1, overflowY: 'auto', padding: '16px 18px',
    display: 'flex', flexDirection: 'column', gap: 12,
  },
  msgUser: {
    alignSelf: 'flex-end', background: 'linear-gradient(135deg,#1a56db,#7c3aed)',
    color: '#fff', borderRadius: '16px 16px 4px 16px',
    padding: '10px 14px', maxWidth: '75%', fontSize: 14, lineHeight: 1.5,
    wordBreak: 'break-word',
  },
  msgAi: {
    alignSelf: 'flex-start', background: 'var(--ai-bubble, #f3f4f6)',
    color: 'var(--text-primary, #111827)', borderRadius: '16px 16px 16px 4px',
    padding: '10px 14px', maxWidth: '80%', fontSize: 14, lineHeight: 1.6,
    wordBreak: 'break-word', whiteSpace: 'pre-wrap',
  },
  msgMeta: { fontSize: 11, color: 'var(--text-secondary, #9ca3af)', marginTop: 4, textAlign: 'right' },
  typingDot: {
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
    background: '#6b7280', margin: '0 2px',
    animation: 'bounce 1.2s infinite',
  },

  // Input area
  inputRow: {
    padding: '12px 16px', borderTop: '1px solid var(--border, #e5e7eb)',
    display: 'flex', gap: 10, alignItems: 'flex-end',
  },
  textarea: {
    flex: 1, resize: 'none', border: '1px solid var(--border, #d1d5db)',
    borderRadius: 10, padding: '10px 14px', fontSize: 14,
    fontFamily: 'inherit', outline: 'none', lineHeight: 1.5,
    background: 'var(--input-bg, #fff)', color: 'var(--text-primary, #111827)',
    maxHeight: 120,
  },
  sendBtn: {
    background: 'linear-gradient(135deg,#1a56db,#7c3aed)',
    color: '#fff', border: 'none', borderRadius: 10,
    padding: '10px 18px', fontWeight: 600, fontSize: 14,
    cursor: 'pointer', flexShrink: 0, height: 42,
    display: 'flex', alignItems: 'center', gap: 6,
  },
  sendBtnDisabled: {
    background: '#e5e7eb', color: '#9ca3af', cursor: 'not-allowed',
  },

  // Quick prompts
  prompts: {
    display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20,
  },
  promptBtn: {
    background: 'var(--card-bg, #fff)', border: '1px solid var(--border, #e5e7eb)',
    borderRadius: 20, padding: '6px 14px', fontSize: 13, cursor: 'pointer',
    color: 'var(--text-primary, #374151)', transition: 'all 0.15s',
  },

  // Urgent issues banner
  urgentBanner: {
    background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10,
    padding: '12px 16px', marginBottom: 16,
  },
  urgentTitle: { fontWeight: 600, color: '#c2410c', fontSize: 13, marginBottom: 6 },
  urgentItem:  { fontSize: 13, color: '#9a3412', display: 'flex', alignItems: 'center', gap: 6 },
};

// ── Quick prompt suggestions ──────────────────────────────────────────────
const QUICK_PROMPTS = [
  '⚠️ What are the most critical issues right now?',
  '📊 Summarize compliance status',
  '🔴 Which devices are offline?',
  '🩹 What patches failed recently?',
  '🛡️ Show critical vulnerabilities',
  '📋 Give me a weekly patch report',
];

// ── Typing indicator ──────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div style={{ ...S.msgAi, padding: '12px 16px' }}>
      <style>{`@keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }`}</style>
      {[0, 0.2, 0.4].map((delay, i) => (
        <span key={i} style={{ ...S.typingDot, animationDelay: `${delay}s` }} />
      ))}
    </div>
  );
}

// ── Format timestamp ──────────────────────────────────────────────────────
function fmtTime(d) {
  return new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ── Main component ────────────────────────────────────────────────────────
export default function AiChat() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm your PatchSphere AI Assistant powered by Gemini. I can answer questions about your endpoints, patch status, compliance, and vulnerabilities using live data.\n\nWhat would you like to know?",
      time: new Date(),
    },
  ]);
  const [input,    setInput]   = useState('');
  const [loading,  setLoading] = useState(false);
  const [summary,  setSummary] = useState(null);
  const [sumLoading, setSumLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const textareaRef    = useRef(null);

  // Load summary on mount
  useEffect(() => {
    api.getAiSummary()
      .then(r => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setSumLoading(false));
  }, []);

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    const userMsg = { role: 'user', content: msg, time: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // Build history for context (last 10 messages, exclude initial greeting)
    const history = messages
      .filter((_, i) => i > 0)
      .slice(-10)
      .map(m => ({ role: m.role, content: m.content }));

    try {
      const res = await api.aiChat(msg, history);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.reply || 'No response received.',
        time: new Date(),
      }]);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${e.response?.data?.error || e.message}. Please try again.`,
        time: new Date(),
        error: true,
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [input, messages, loading]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "Chat cleared. How can I help you with your patch management?",
      time: new Date(),
    }]);
  };

  // ── Summary card values ──────────────────────────────────────────────
  const cards = summary ? [
    { label: 'Online Devices',  value: `${summary.onlineDevices}/${summary.totalDevices}`, color: summary.onlineDevices > 0 ? '#16a34a' : '#dc2626' },
    { label: 'Critical Vulns',  value: summary.criticalVulns,  color: summary.criticalVulns > 0 ? '#dc2626' : '#16a34a' },
    { label: 'Failed Patches',  value: summary.failedPatches,  color: summary.failedPatches > 0 ? '#ea580c' : '#16a34a' },
    { label: 'Pending Patches', value: summary.pendingPatches, color: '#d97706' },
    { label: 'Compliance',      value: `${summary.complianceScore}%`, color: summary.complianceScore >= 80 ? '#16a34a' : summary.complianceScore >= 50 ? '#d97706' : '#dc2626' },
  ] : [];

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.headerIcon}>🤖</div>
        <div>
          <div style={S.headerTitle}>PatchSphere AI Assistant</div>
          <div style={S.headerSub}>Powered by Gemini · Live data from your endpoints</div>
        </div>
      </div>

      {/* Summary cards */}
      {!sumLoading && summary && (
        <>
          <div style={S.summaryRow}>
            {cards.map(c => (
              <div key={c.label} style={S.card}>
                <div style={S.cardLabel}>{c.label}</div>
                <div style={{ ...S.cardValue, color: c.color }}>{c.value}</div>
              </div>
            ))}
          </div>

          {/* Urgent issues banner */}
          {summary.urgentIssues?.length > 0 && (
            <div style={S.urgentBanner}>
              <div style={S.urgentTitle}>⚠️ Urgent Issues Detected</div>
              {summary.urgentIssues.map((issue, i) => (
                <div key={i} style={S.urgentItem}>
                  <span>•</span> {issue}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Quick prompts */}
      <div style={S.prompts}>
        {QUICK_PROMPTS.map(p => (
          <button
            key={p}
            style={S.promptBtn}
            onClick={() => sendMessage(p.replace(/^[^\s]+ /, ''))}
            onMouseEnter={e => { e.target.style.borderColor = '#1a56db'; e.target.style.color = '#1a56db'; }}
            onMouseLeave={e => { e.target.style.borderColor = 'var(--border, #e5e7eb)'; e.target.style.color = 'var(--text-primary, #374151)'; }}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Chat window */}
      <div style={S.chatWrap}>
        <div style={S.chatHeader}>
          <div style={S.chatTitle}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
            AI Assistant Online
          </div>
          <button style={S.clearBtn} onClick={clearChat}>Clear chat</button>
        </div>

        {/* Messages */}
        <div style={S.messages}>
          {messages.map((m, i) => (
            <div key={i}>
              <div style={m.role === 'user' ? S.msgUser : {
                ...S.msgAi,
                ...(m.error ? { background: '#fee2e2', color: '#dc2626' } : {}),
              }}>
                {m.content}
              </div>
              <div style={{ ...S.msgMeta, textAlign: m.role === 'user' ? 'right' : 'left' }}>
                {m.role === 'user' ? 'You' : '🤖 PatchSphere AI'} · {fmtTime(m.time)}
              </div>
            </div>
          ))}
          {loading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={S.inputRow}>
          <textarea
            ref={textareaRef}
            style={S.textarea}
            placeholder="Ask about devices, patches, compliance, vulnerabilities..."
            value={input}
            rows={1}
            onChange={e => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKey}
            disabled={loading}
          />
          <button
            style={{ ...S.sendBtn, ...(loading || !input.trim() ? S.sendBtnDisabled : {}) }}
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
          >
            {loading ? '...' : '↑ Send'}
          </button>
        </div>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-secondary, #9ca3af)', marginTop: 12, textAlign: 'center' }}>
        AI responses use live data from your PatchSphere database · Powered by Google Gemini
      </p>
    </div>
  );
}
